import { DOCUMENT, Injectable, computed, inject, signal } from '@angular/core';
import { THEME_PREFERENCE_STORE } from './theme-preference.store';
import { DEFAULT_THEME, Theme, isTheme } from './theme.types';

/**
 * The single runtime authority for the colour theme.
 *
 * It owns the `data-theme` attribute on <html> (which selects the token values in
 * _theme-tokens.scss) and the persisted preference. Components read `theme()` /
 * `isDark()` and call `setTheme()` / `toggle()`; they never touch the attribute or
 * the storage directly.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly store = inject(THEME_PREFERENCE_STORE);

  private readonly current = signal<Theme>(this.resolveInitialTheme());

  /** Active theme. */
  readonly theme = this.current.asReadonly();
  readonly isDark = computed(() => this.current() === 'dark');

  constructor() {
    this.apply(this.current());
  }

  setTheme(theme: Theme): void {
    if (!isTheme(theme) || theme === this.current()) {
      return;
    }
    this.current.set(theme);
    this.apply(theme);
    this.store.write(theme);
  }

  toggle(): void {
    this.setTheme(this.current() === 'dark' ? 'light' : 'dark');
  }

  /**
   * The pre-bootstrap script in index.html has usually applied the stored theme
   * already; fall back to the store and then the default.
   */
  private resolveInitialTheme(): Theme {
    const applied = this.document.documentElement.getAttribute('data-theme');
    if (isTheme(applied)) {
      return applied;
    }
    return this.store.read() ?? DEFAULT_THEME;
  }

  private apply(theme: Theme): void {
    const root = this.document.documentElement;
    root.setAttribute('data-theme', theme);
    // Keeps the UA colour scheme in step before/while the stylesheet applies.
    this.document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  }
}
